import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Clock as ClockIcon, List as ListIcon, ArrowRight } from "lucide-react";
import { CLOCK, currentClockSlot } from "@/data/clock";
import { elementColor } from "@/data/theme";

// Relógio circular dos 12 órgãos (ciclo de 24h). Conteúdo fiel ao PDF (pág. 6).
export default function OrganClock({ size = 360, onSelect }) {
  const [viewMode, setViewMode] = useState("clock");
  const [nowSlot, setNowSlot] = useState(currentClockSlot());
  const [active, setActive] = useState(nowSlot?.orgaoId);
  const [clockSize, setClockSize] = useState(size);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setNowSlot(currentClockSlot());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        const available = Math.min(size, window.innerWidth - 40);
        setClockSize(Math.max(280, available));
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [size]);

  const R = clockSize / 2;
  const isSmall = clockSize < 340;
  const ringR = R - (isSmall ? 28 : 34);

  const handle = (slot) => {
    setActive(slot.orgaoId);
    if (onSelect) onSelect(slot);
  };

  const activeSlot = CLOCK.find((s) => s.orgaoId === active) || nowSlot;

  return (
    <div className="flex flex-col items-center gap-6 w-full">
      {/* SELETOR DE VISUALIZAÇÃO: RELÓGIO / LISTA */}
      <div className="flex justify-center w-full" data-testid="clock-view-controls">
        <div
          role="group"
          aria-label="Modo de visualização do relógio dos órgãos"
          className="inline-flex items-center rounded-2xl border border-border bg-secondary/50 p-1 shadow-sm"
        >
          <button
            type="button"
            data-testid="clock-toggle-relogio"
            aria-pressed={viewMode === "clock"}
            onClick={() => setViewMode("clock")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
              viewMode === "clock"
                ? "bg-card text-foreground shadow-sm font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ClockIcon className="h-4 w-4" />
            <span>Relógio</span>
          </button>

          <button
            type="button"
            data-testid="clock-toggle-lista"
            aria-pressed={viewMode === "list"}
            onClick={() => setViewMode("list")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
              viewMode === "list"
                ? "bg-card text-foreground shadow-sm font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ListIcon className="h-4 w-4" />
            <span>Lista</span>
          </button>
        </div>
      </div>

      {viewMode === "clock" ? (
        <div className="flex flex-col items-center gap-6 w-full" data-testid="clock-circular-view">
          <div className="w-full flex justify-center py-2">
            <div
              className="relative shrink-0 aspect-square"
              style={{ width: clockSize, height: clockSize }}
            >
              <div className="absolute inset-[6%] rounded-full border border-gold/25 pointer-events-none" />
              <div className="absolute inset-[20%] rounded-full border border-gold/15 pointer-events-none" />

              {/* Centro: órgão selecionado */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center px-4 sm:px-8">
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Selecionado</p>
                  <p className="mt-0.5 sm:mt-1 font-serif text-base sm:text-xl font-semibold text-foreground leading-tight">
                    {activeSlot?.orgao}
                  </p>
                  <p className="text-xs sm:text-sm text-gold font-medium">{activeSlot?.periodo}</p>
                </div>
              </div>

              {CLOCK.map((slot, i) => {
                const angle = (i / CLOCK.length) * 2 * Math.PI - Math.PI / 2;
                const x = R + ringR * Math.cos(angle);
                const y = R + ringR * Math.sin(angle);
                const c = elementColor(slot.elemento);
                const isActive = slot.orgaoId === active;
                const isNow = slot.orgaoId === nowSlot?.orgaoId;
                return (
                  <button
                    key={slot.orgaoId}
                    data-testid={`clock-slot-${slot.orgaoId}`}
                    onClick={() => handle(slot)}
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 flex-col items-center justify-center rounded-full text-center transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gold"
                    style={{
                      left: x,
                      top: y,
                      backgroundColor: isActive ? c.primary : c.soft,
                      color: isActive ? "#fff" : c.text,
                      boxShadow: isNow ? `0 0 0 3px hsl(var(--gold))` : "none",
                    }}
                  >
                    <span className="text-[8px] sm:text-[9px] font-semibold leading-none">{slot.periodo.split(" ")[0]}</span>
                    <span className="mt-0.5 px-0.5 text-[8px] sm:text-[9px] font-medium leading-tight line-clamp-2">
                      {slot.orgao}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {activeSlot && (
            <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 text-center">
              <p className="text-sm text-foreground leading-relaxed">{activeSlot.descricao}</p>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{activeSlot.cuidado}</p>
              <button
                data-testid="clock-goto-organ"
                onClick={() => navigate(`/orgaos/${activeSlot.orgaoId}`)}
                className="mt-4 inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
              >
                Abrir ficha de {activeSlot.orgao}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full max-w-md space-y-3" data-testid="clock-list-view">
          {CLOCK.map((slot) => {
            const c = elementColor(slot.elemento);
            const isNow = slot.orgaoId === nowSlot?.orgaoId;
            return (
              <div
                key={slot.orgaoId}
                data-testid={`clock-list-item-${slot.orgaoId}`}
                className={`rounded-2xl border p-4 sm:p-5 transition-all ${
                  isNow
                    ? "border-gold bg-gold/5 shadow-sm"
                    : "border-border bg-card hover:border-gold/30"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="font-serif text-base sm:text-lg font-semibold"
                      style={{ color: c.primary }}
                    >
                      {slot.periodo}
                    </span>
                    {isNow && (
                      <span className="rounded-full bg-gold/20 px-2.5 py-0.5 text-[10px] font-semibold text-gold tracking-wide">
                        Agora
                      </span>
                    )}
                  </div>
                  <span
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: c.primary }}
                    title={`Elemento ${slot.elemento}`}
                  />
                </div>

                <h4 className="mt-2 text-base font-medium text-foreground">{slot.orgao}</h4>
                <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {slot.descricao}
                </p>
                <p className="mt-1.5 text-xs sm:text-sm leading-relaxed" style={{ color: c.text }}>
                  {slot.cuidado}
                </p>

                <div className="mt-3.5 pt-2.5 border-t border-border/50 flex justify-end">
                  <Link
                    to={`/orgaos/${slot.orgaoId}`}
                    data-testid={`clock-list-link-${slot.orgaoId}`}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gold hover:gap-2 transition-all"
                  >
                    Abrir ficha de {slot.orgao} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
